import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getLegalDocument } from "../services/api";
import type { LegalDocKey, LegalLang } from "../constants/legal";

// Module-level cache — survives re-renders, cleared on full page reload
const sessionCache = new Map<string, string>();

interface UseLegalDocumentResult {
  html: string | null;
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

export const useLegalDocument = (doc: LegalDocKey): UseLegalDocumentResult => {
  const { i18n } = useTranslation();

  // fr has no dedicated legal HTML — fall back to English
  const lang: LegalLang = i18n.language.startsWith("es") ? "es" : "en";
  const cacheKey = `${doc}:${lang}`;

  const [html, setHtml] = useState<string | null>(
    sessionCache.get(cacheKey) ?? null
  );
  const [isLoading, setIsLoading] = useState(!sessionCache.has(cacheKey));
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (sessionCache.has(cacheKey)) {
      // Intentional: serve the cached document synchronously.
      /* eslint-disable react-hooks/set-state-in-effect */
      setHtml(sessionCache.get(cacheKey)!);
      setIsLoading(false);
      /* eslint-enable react-hooks/set-state-in-effect */
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getLegalDocument(doc, lang)
      .then((res) => {
        if (cancelled) return;
        sessionCache.set(cacheKey, res.html);
        setHtml(res.html);
      })
      .catch(() => {
        if (cancelled) return;
        setError("legal.error");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, attempt]);

  const retry = () => setAttempt((n) => n + 1);

  return { html, isLoading, error, retry };
};
